import { AsclepiusApiError } from "@/composables/api/asclepius"

/** `app_error_code`s of `/v1/patient-conversations`; they are not part of the SDK's shared error enum. */
export const PatientConversationErrorCode = {
  StaffNotAllowed: "staff_not_allowed",
  NotFound: "patient_conversation_not_found",
  NotOpen: "patient_conversation_not_open",
  MemberHasNoAccount: "member_has_no_account",
  MemberIsMinor: "member_is_minor",
  MemberStateNotAllowed: "member_state_not_allowed",
  Closed: "patient_conversation_closed",
  SenderNotAssignee: "sender_not_assignee",
  InvalidStatusTransition: "invalid_status_transition",
  VisibilityNotAllowed: "visibility_not_allowed",
  MentionNotAllowed: "mention_not_allowed",
  AttachmentNotAttachable: "attachment_not_attachable",
  TooManyAttachments: "too_many_attachments",
  FileTypeNotAllowed: "file_type_not_allowed",
  FileTooLarge: "file_too_large"
} as const

const ERROR_MESSAGES: Record<string, string> = {
  [PatientConversationErrorCode.StaffNotAllowed]: "This staff member does not have access to patient messaging",
  [PatientConversationErrorCode.NotFound]: "This conversation no longer exists",
  [PatientConversationErrorCode.NotOpen]: "Only open conversations can be assigned",
  [PatientConversationErrorCode.MemberHasNoAccount]:
    "This patient has no Vitable account yet, so they cannot receive messages",
  [PatientConversationErrorCode.MemberIsMinor]: "Minors cannot be messaged",
  [PatientConversationErrorCode.MemberStateNotAllowed]:
    "Patient messaging is not available in this patient's state yet",
  [PatientConversationErrorCode.Closed]: "This conversation is closed",
  [PatientConversationErrorCode.SenderNotAssignee]: "Only the assignee can message the patient",
  [PatientConversationErrorCode.InvalidStatusTransition]:
    "This conversation's status changed in the meantime. Refresh and try again",
  [PatientConversationErrorCode.VisibilityNotAllowed]: "This message type is not allowed here",
  [PatientConversationErrorCode.MentionNotAllowed]: "Pages are only allowed in internal notes",
  [PatientConversationErrorCode.AttachmentNotAttachable]:
    "An attachment is no longer available. Remove it and attach the file again",
  [PatientConversationErrorCode.TooManyAttachments]: "Up to 5 attachments per message",
  [PatientConversationErrorCode.FileTypeNotAllowed]: "Only JPEG, PNG, WebP images and PDFs can be attached",
  [PatientConversationErrorCode.FileTooLarge]: "Files must be 10 MB or smaller"
}

// The API's wording for these is technical ("file must be at most 10485760 bytes"); ours is for providers
const PREFER_OUR_WORDING: ReadonlySet<string> = new Set([
  PatientConversationErrorCode.Closed,
  PatientConversationErrorCode.SenderNotAssignee,
  PatientConversationErrorCode.InvalidStatusTransition,
  PatientConversationErrorCode.VisibilityNotAllowed,
  PatientConversationErrorCode.MentionNotAllowed,
  PatientConversationErrorCode.AttachmentNotAttachable,
  PatientConversationErrorCode.TooManyAttachments,
  PatientConversationErrorCode.FileTypeNotAllowed,
  PatientConversationErrorCode.FileTooLarge
])

/** Why a member cannot be messaged, from the eligibility endpoint's `reason`. */
export function describeIneligibilityReason(reason: string | null | undefined): string {
  return describePatientConversationErrorCode(reason, "This patient cannot be messaged")
}

/** For bulk-result rows, which carry a code but no message. */
export function describePatientConversationErrorCode(code: string | null | undefined, fallback: string): string {
  return (code && ERROR_MESSAGES[code]) || fallback
}

/**
 * For a failed call: the API's message names the person or conversation involved, so it is preferred, except
 * for the codes whose API wording is technical.
 */
export function describePatientConversationError(error: unknown): string {
  if (error instanceof AsclepiusApiError && error.appErrorCode && PREFER_OUR_WORDING.has(error.appErrorCode))
    return ERROR_MESSAGES[error.appErrorCode]
  if (error instanceof AsclepiusApiError && error.message) return error.message
  if (error instanceof AsclepiusApiError)
    return describePatientConversationErrorCode(error.appErrorCode, "Request failed")
  return error instanceof Error ? error.message : "Something went wrong"
}
